import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Table,
  notification,
  Form,
  Collapse,
  Row,
  Col,
} from 'antd';
import {
  DeleteOutlined,
  ExportOutlined, ImportOutlined, LockOutlined, PlusOutlined, RedoOutlined, SettingOutlined, UnlockOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ScheduleOfValues } from 'ontraccr-common';

import DraggableTable from './DraggableTable';
import {
  getScheduleOfValueSections,
  getProjectScheduleOfValues,
  updateProjectScheduleOfValues,
  swapRowsProjectScheduleOfValues,
  updateIndividualProject,
  deleteProjectScheduleOfValueRow,
  deleteScheduleOfValueSection,
  submitProjectScheduleOfValuesInvoiceHoldback,
  revertProjectScheduleOfValuesHoldbackInvoice,
} from '../state/projects.actions';
import ProjectScheduleOfValuesResetDrawer from './ProjectScheduleOfValuesResetDrawer';
import {
  getScheduleOfValueColumns,
  getLiveSummaryColumns,
  ALL_OPTIONAL_SOV_COLUMNS,
} from './ScheduleOfValuesColumns';
import ScheduleOfValuesColumnChanger from './ScheduleOfValuesColumnChanger';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import OnTraccrButtonDropdown from '../../common/buttons/OnTraccrButtonDropdown';
import ScheduleOfValuesLog from './ScheduleOfValuesLog';
import ScheduleOfValuesAddRow from './ScheduleOfValuesAddRow';
import ScheduleOfValuesSubmissionReview from './ScheduleOfValuesSubmissionReview';
import ScheduleOfValuesPDFPreview from './PDF/ScheduleOfValuesPDFPreview';
import HistoryDrawer from './History/HistoryDrawer';
import Permissions from '../../auth/Permissions';
import { getIdMap, isNullOrUndefined } from '../../helpers/helpers';
import { getFileType } from '../../files/fileHelpers';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import {
  scheduleOfValuesData,
  filterScheduleOfValues,
  getSubContractsWithTotalChanges,
  getSovParentKeys,
  getSovRowClassName,
  getSovTreeColumns,
} from './helpers';
import useSovExpandedRowKeys from './useSovExpandedRowKeys';
import ScheduleOfValuesPastBillReview from './ScheduleOfValuesPastBillReview';
import ScheduleOfValuesTemplateImport from './ScheduleOfValuesTemplateImport';
import ScheduleOfValuesExcelImportDrawer from './Import/ScheduleOfValuesExcelImportDrawer';
import ProjectScheduleOfValuesSubContract from './ProjectScheduleOfValuesSubContract';
import ScheduleOfValuesAddSectionModal from './ScheduleOfValuesAddSectionModal';
import ScheduleOfValuesNotesModal from './ScheduleOfValuesNotesModal';
import useToggle from '../../common/hooks/useToggle';
import Colors from '../../constants/Colors';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import { currencyFormatter } from '../../helpers/inputParsers';

const { Panel } = Collapse;

const DEFAULT_HOLDBACK_AMOUNT = 0.10;
const BASE_PANEL = 'baseContract';
const SUB_CONTRACT_PANEL = 'subContracts';
const CHANGES_PANEL = 'changes';
const NON_HOLDBACK_PANEL = 'nonHoldbackItems';
const DEFAULT_ACTIVE_PANELS = [BASE_PANEL, CHANGES_PANEL, NON_HOLDBACK_PANEL];
const SUB_ITEM_DRAWER = 'addRowSubItem';
const TEMPLATE_IMPORT_DRAWER = 'import';
const EXCEL_IMPORT_DRAWER = 'importExcel';
const IMPORT_MENU_ITEMS = [
  { key: TEMPLATE_IMPORT_DRAWER, label: 'From Template' },
  { key: EXCEL_IMPORT_DRAWER, label: 'From Excel' },
];

// Re-derive a tree bucket after local edits, optionally swapping in one parent's children.
const rebuildSovBucket = (rows, parentRowId, newChildren) => {
  const withNewChildren = (rows ?? []).map((row) => (
    parentRowId && ScheduleOfValues.getSovRowId(row) === parentRowId
      ? { ...row, children: newChildren }
      : row
  ));

  return ScheduleOfValues.buildSovTree(
    ScheduleOfValues.flattenSovTree(withNewChildren)
      .map(ScheduleOfValues.stripSovTreeFields),
  );
};

const renumberSovRows = (rows) => rows.map((item, index) => ({
  ...item,
  oldItemNumber: isNullOrUndefined(item.oldItemNumber)
    ? item.itemNumber
    : item.oldItemNumber,
  itemNumber: index + 1,
  isChangeOrder: !!item.isChangeOrder,
  isSubContract: !!item.isSubContract,
  updatedField: 'itemNumber',
  excludeHoldback: !!item.excludeHoldback,
}));

export default function ProjectScheduleOfValues({
  projectId,
  templateId,
  containerStyle = {},
  readOnly = false,
}) {
  const dispatch = useDispatch();

  const {
    scheduleOfValues = {},
    totalBaseContractValue,
    holdbackPercentage,
    isScheduleOfValueLocked,
    sovTaxPercentage,
    divisionId,
  } = useSelector((state) => {
    const currentProject = state.projects.project.id === projectId
      ? state.projects.project
      : state.projects.projects.find((project) => project.id === projectId);

    return {
      ...state.projects,
      totalBaseContractValue: currentProject
        ? currentProject.totalContractAmount
        : 0,
      holdbackPercentage: currentProject
        ? currentProject.holdbackPercentage
        : 0,
      isScheduleOfValueLocked: currentProject
        ? currentProject.isScheduleOfValueLocked
        : false,
      sovTaxPercentage: currentProject
        ? currentProject.sovTaxPercentage
        : null,
      divisionId: currentProject?.divisionId,
    };
  });
  const scheduleOfValueSections = useSelector((state) => state.projects.scheduleOfValueSections);

  const [visibleDrawer, setVisibleDrawer] = useState();
  const [activePanels, setActivePanels] = useState(DEFAULT_ACTIVE_PANELS);
  const [selectedRecord, setSelectedRecord] = useState();
  const [baseContractValues, setBaseContractValues] = useState([]);
  const [subContractValues, setSubContractValues] = useState([]);
  const [changeOrderValues, setChangeOrderValues] = useState([]);
  const [liveSummaryValues, setLiveSummaryValues] = useState([]);
  const [nonHoldbackValues, setNonHoldbackValues] = useState([]);
  const [subContractCOs, setSubContractCOs] = useState({});
  const [logValues, setLogValues] = useState([]);
  const [currentHoldbackPercentage, setCurrentHoldbackPercentage] = useState(holdbackPercentage);
  const [newItemNumbers, setNewItemNumbers] = useState({
    baseContract: 1,
    subContract: 1,
    subContractCOs: 1,
    changeOrder: 1,
    nonHoldback: 1,
  });
  const [sovSnapshot, setSOVSnapshot] = useState(null);
  const [sectionValueMap, setSectionValueMap] = useState({});
  const [showLogDrawer, setShowLogDrawer] = useState(false);

  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState({});
  const [isLocked, setIsLocked] = useState(isScheduleOfValueLocked);
  const [selectedSection, setSelectedSection] = useState();
  const [selectedSovBill, setSelectedSovBill] = useState();
  const [selectedSovBillData, setSelectedSovBillData] = useState();
  const [selectedNoteRow, setSelectedNoteRow] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isInvoiceHoldback, setIsInvoiceHoldback] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(ALL_OPTIONAL_SOV_COLUMNS);
  const [subItemParent, setSubItemParent] = useState();

  const parentRowKeys = useMemo(() => getSovParentKeys([
    baseContractValues,
    ...Object.values(sectionValueMap),
  ]), [baseContractValues, sectionValueMap]);

  const treeExpandable = useSovExpandedRowKeys(parentRowKeys);

  useEffect(() => {
    const stored = window.localStorage.getItem(`scheduleofvalue-visible-columns-${projectId || templateId}`);
    if (stored) {
      setVisibleColumns(JSON.parse(stored));
    }
  }, [projectId, templateId]);

  const onColumnVisibilityChange = useCallback((selected) => {
    setVisibleColumns(selected);
    window.localStorage.setItem(
      `scheduleofvalue-visible-columns-${projectId || templateId}`,
      JSON.stringify(selected),
    );
  }, [projectId, templateId]);

  const clearSelectedFile = useCallback(() => setSelectedFile({}), []);
  const {
    toggle: toggleAddSectionModal,
    isToggled: isAddSectionModalToggled,
  } = useToggle();

  const switchDrawer = useCallback((drawer) => {
    if (visibleDrawer === 'export') {
      setSOVSnapshot(null);
    }

    if (visibleDrawer?.includes('addRow')) setSelectedSection();
    if (drawer !== SUB_ITEM_DRAWER) setSubItemParent();

    setFiles([]);
    clearSelectedFile();
    setVisibleDrawer(drawer);
  }, [visibleDrawer, clearSelectedFile]);

  const onCloseDrawer = useCallback(() => switchDrawer(), [switchDrawer]);

  const onHistoryClick = useCallback((record) => {
    switchDrawer('history');
    setSelectedRecord(record);
  }, [switchDrawer]);

  const onInfoClick = useCallback((record) => {
    switchDrawer('sub-contract');
    setSelectedRecord(record);
  }, [switchDrawer]);

  const onAddSubItemClick = useCallback((record) => {
    setSubItemParent(record);
    switchDrawer(SUB_ITEM_DRAWER);
  }, [switchDrawer]);

  const onDeleteClick = useCallback((record) => {
    const onDelete = () => {
      dispatch(deleteProjectScheduleOfValueRow({
        projectId,
        templateId,
        rowId: record.rowId,
      }));
    };

    const { subItemCount = 0 } = record ?? {};
    const isSubItem = ScheduleOfValues.isSubItem(record);
    const parent = isSubItem
      ? [...baseContractValues, ...Object.values(sectionValueMap).flat()]
        .find((row) => ScheduleOfValues.getSovRowId(row) === record.parentRowId)
      : null;
    const parentNumber = parent?.lineNumber ?? parent?.itemNumber ?? '';
    // The parent takes the last sub-item's amounts over, so only a sub-item that
    // leaves siblings behind takes its billed amounts out of the parent's totals.
    const isLastSubItem = isSubItem && (parent?.subItemCount ?? 0) <= 1;
    const hasBilledValues = isSubItem
      && !isLastSubItem
      && (!!Number(record.previousBillings) || !!Number(record.progressToDate));

    return new Promise((resolve) => {
      CustomConfirmModal({
        title: 'Delete Confirmation',
        content: (
          <p>
            Are you sure you wish to delete this row?
            {!!subItemCount && (
              <>
                <br />
                <br />
                {`This will also delete ${subItemCount} sub-item(s).`}
              </>
            )}
            {hasBilledValues && (
              <>
                <br />
                <br />
                {`This sub-item has billed amounts. Deleting it removes them from Item ${parentNumber}'s totals.`}
              </>
            )}
            {isLastSubItem && (
              <>
                <br />
                <br />
                {`Item ${parentNumber} has no other sub-items. It will take over this sub-item's amounts and become editable again.`}
              </>
            )}
          </p>
        ),
        okText: 'Delete',
        cancelText: 'Cancel',
        async onOk() {
          resolve(await onDelete());
        },
        onCancel() {
          resolve();
        },
      });
    });
  }, [projectId, templateId, baseContractValues, sectionValueMap]);

  const isReadOnly = !Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE') || readOnly;
  const columns = useMemo(
    () => getScheduleOfValueColumns({
      onHistory: onHistoryClick,
      onDelete: onDeleteClick,
      onNotesSelect: setSelectedNoteRow,
      onAddSubItem: onAddSubItemClick,
      readOnly: isReadOnly,
      liveSummaryValues,
      isScheduleOfValueLocked,
      visibleColumns,
    }),
    [
      onHistoryClick,
      liveSummaryValues,
      isReadOnly,
      isScheduleOfValueLocked,
      onDeleteClick,
      onAddSubItemClick,
      visibleColumns,
    ],
  );

  const baseContractColumns = useMemo(
    () => getSovTreeColumns(columns, baseContractValues),
    [columns, baseContractValues],
  );

  const sectionColumnMap = useMemo(() => {
    const map = {};
    Object.entries(sectionValueMap).forEach(([sectionId, rows]) => {
      map[sectionId] = getSovTreeColumns(columns, rows);
    });
    return map;
  }, [columns, sectionValueMap]);

  const baseContractRowClassName = useMemo(
    () => getSovRowClassName(baseContractValues),
    [baseContractValues],
  );

  const sectionRowClassNameMap = useMemo(() => {
    const map = {};
    Object.entries(sectionValueMap).forEach(([sectionId, rows]) => {
      map[sectionId] = getSovRowClassName(rows);
    });
    return map;
  }, [sectionValueMap]);

  const subcontractColumns = useMemo(
    () => getScheduleOfValueColumns({
      onInfo: onInfoClick,
      onHistory: onHistoryClick,
      onDelete: onDeleteClick,
      onNotesSelect: setSelectedNoteRow,
      readOnly: isReadOnly,
      isSubContract: true,
      liveSummaryValues,
      isScheduleOfValueLocked,
      visibleColumns,
    }),
    [
      onHistoryClick,
      onInfoClick,
      liveSummaryValues,
      isReadOnly,
      isScheduleOfValueLocked,
      onDeleteClick,
      visibleColumns,
    ],
  );

  const changesColumns = useMemo(() => (
    getScheduleOfValueColumns({
      onHistory: onHistoryClick,
      onDelete: onDeleteClick,
      onNotesSelect: setSelectedNoteRow,
      readOnly: isReadOnly,
      liveSummaryValues,
      isScheduleOfValueLocked,
      isChanges: true,
      visibleColumns,
    })
  ), [
    onHistoryClick,
    liveSummaryValues,
    isReadOnly,
    isScheduleOfValueLocked,
    onDeleteClick,
    visibleColumns,
  ]);

  // Non-holdback rows cannot hold sub-items, so they get no Sub-Item column.
  const nonHoldbackColumns = useMemo(() => (
    getScheduleOfValueColumns({
      onHistory: onHistoryClick,
      onDelete: onDeleteClick,
      onNotesSelect: setSelectedNoteRow,
      readOnly: isReadOnly,
      liveSummaryValues,
      isScheduleOfValueLocked,
      visibleColumns,
    })
  ), [
    onHistoryClick,
    liveSummaryValues,
    isReadOnly,
    isScheduleOfValueLocked,
    onDeleteClick,
    visibleColumns,
  ]);

  const subContractsWithTotalChanges = useMemo(() => (
    getSubContractsWithTotalChanges({
      subContractValues,
      subContractCOs,
    })
  ), [subContractValues, subContractCOs]);

  const onHoldbackInvoiceClicked = useCallback(() => {
    setIsInvoiceHoldback(true);
    const holdbackAmount = liveSummaryValues[0]?.holdbackAmount;
    CustomConfirmModal({
      title: 'Invoice Holdback',
      content: (
        <div>
          <p>Are you sure you want to invoice the holdback?</p>
          <Row justify="center" align="middle">
            <Col>
              <div style={{ fontWeight: 650 }}>Holdback Release:&nbsp;&nbsp;</div>
            </Col>
            <Col>
              {holdbackAmount ? currencyFormatter(holdbackAmount) : 0}
            </Col>
          </Row>
        </div>
      ),
      onOk: async () => {
        const result = await dispatch(submitProjectScheduleOfValuesInvoiceHoldback(projectId, {
          holdbackAmount,
        }));
        if (result) {
          setVisibleDrawer('export');
          setSOVSnapshot({
            baseContractValues,
            changeOrderValues,
            liveSummaryValues: [{ ...(liveSummaryValues[0] || {}), holdbackAmount }],
            nonHoldbackValues,
            sectionValueMap,
            currentHoldbackPercentage,
            subContractValues: subContractsWithTotalChanges,
            subContractCOs,
          });
        }
      },
      onCancel: () => {
        setIsInvoiceHoldback(false);
      },
    });
  }, [
    liveSummaryValues,
    projectId,
    baseContractValues,
    changeOrderValues,
    liveSummaryValues,
    nonHoldbackValues,
    sectionValueMap,
    currentHoldbackPercentage,
    subContractsWithTotalChanges,
    subContractCOs,
  ]);

  const liveSummaryColumns = useMemo(
    () => getLiveSummaryColumns({
      onClick: () => setVisibleDrawer('review'),
      readOnly: isReadOnly || templateId,
      showHoldback: currentHoldbackPercentage,
      isScheduleOfValueLocked,
      onHoldbackInvoiceClicked,
    }),
    [currentHoldbackPercentage, isReadOnly, isScheduleOfValueLocked, templateId, onHoldbackInvoiceClicked],
  );

  useEffect(() => {
    dispatch(getProjectScheduleOfValues({
      projectId,
      templateId,
    }));
    dispatch(getScheduleOfValueSections({
      projectId,
      templateId,
    }));
  }, [projectId, templateId]);

  const scheduleOfValueSectionMap = useMemo(() => (
    getIdMap(scheduleOfValueSections)
  ), [scheduleOfValueSections]);

  useEffect(() => {
    const {
      newBaseContractValues,
      newSubContractValues,
      newSubContractCOs,
      newChangeOrderValues,
      newNonHoldbackItems,
      newLiveSummaryValue,
      maxItemNumbers,
      sectionValueMap: newSectionValueMap,
    } = scheduleOfValuesData({
      projectId,
      templateId,
      totalBaseContractValue,
      holdbackPercentage,
      scheduleOfValues,
      setLogValues,
      scheduleOfValueSectionMap,
    });
    setBaseContractValues(newBaseContractValues);
    setSubContractValues(newSubContractValues);
    setSubContractCOs(newSubContractCOs);
    setChangeOrderValues(newChangeOrderValues);
    setNonHoldbackValues(newNonHoldbackItems);
    setLiveSummaryValues([newLiveSummaryValue]);
    setNewItemNumbers(maxItemNumbers);
    setSectionValueMap(newSectionValueMap);

    let newActivePanels = [...DEFAULT_ACTIVE_PANELS];

    if (newSubContractValues.length) {
      newActivePanels = newActivePanels.concat(SUB_CONTRACT_PANEL);
    }

    if (Object.keys(newSectionValueMap).length) {
      newActivePanels = newActivePanels.concat(Object.keys(newSectionValueMap));
    }

    setActivePanels(newActivePanels);
  }, [
    scheduleOfValues,
    totalBaseContractValue,
    holdbackPercentage,
    projectId,
    templateId,
    scheduleOfValueSectionMap,
  ]);

  const handleSubmit = useCallback(async (payload) => {
    setIsLoading(true);
    const isSubItem = visibleDrawer === SUB_ITEM_DRAWER;
    const updatePayload = {
      ...payload,
      isSubContract: visibleDrawer === 'sub-contract' || visibleDrawer === 'addRowSubContract',
      isChangeOrder: payload.isChangeOrder || visibleDrawer === 'addRowChangeOrder',
      excludeHoldback: visibleDrawer === 'addRowNonHoldbackItems',
      sectionId: (isSubItem ? subItemParent?.sectionId : selectedSection?.id) || null,
    };

    // A sub-item is numbered within its parent rather than within the table.
    if (isSubItem) {
      updatePayload.parentRowId = subItemParent?.rowId;
      updatePayload.itemNumber = (subItemParent?.children?.length ?? 0) + 1;
    }

    const result = await dispatch(updateProjectScheduleOfValues({
      projectId,
      templateId,
      payload: updatePayload,
    }));
    setIsLoading(false);

    if (result && isSubItem) {
      notification.close('addRowKey');
      onCloseDrawer();
    } else if (result) {
      notification.close('addRowKey');
      if (visibleDrawer !== 'sub-contract') onCloseDrawer();
      const newVals = { ...newItemNumbers };
      if (updatePayload.isChangeOrder) {
        if (updatePayload.isSubContract) {
          newVals.subContractCOs += 1;
        } else {
          newVals.changeOrder += 1;
        }
      } else if (updatePayload.isSubContract) {
        newVals.subContract += 1;
      } else if (updatePayload.excludeHoldback) {
        newVals.nonHoldback += 1;
      } else if (updatePayload.sectionId) {
        if (!newVals[updatePayload.sectionId]) {
          newVals[updatePayload.sectionId] = 2;
        } else {
          newVals[updatePayload.sectionId] += 1;
        }
      } else {
        newVals.baseContract += 1;
      }

      setNewItemNumbers(newVals);
    }
    if (visibleDrawer?.includes('addRow')) setSelectedSection();
    return result;
  }, [
    projectId,
    templateId,
    liveSummaryValues,
    newItemNumbers,
    visibleDrawer,
    selectedSection,
    subItemParent,
  ]);

  const onSetData = useCallback((type, newData) => {
    const updatedData = renumberSovRows(newData);

    dispatch(swapRowsProjectScheduleOfValues({
      projectId,
      templateId,
      updatedRows: updatedData.map(ScheduleOfValues.stripSovTreeFields),
    }));

    switch (type) {
      case 'baseContract':
        return setBaseContractValues(rebuildSovBucket(updatedData));
      case 'subContract':
        return setSubContractValues(updatedData);
      case 'changeOrder':
        return setChangeOrderValues(updatedData);
      case 'subContractChangeOrders': {
        const newSubContractCOs = { ...subContractCOs };
        // parentRowId will be the same for all
        const [{ parentRowId } = {}] = updatedData;
        newSubContractCOs[parentRowId] = {
          ...newSubContractCOs[parentRowId] ?? {},
          changes: updatedData,
        };
        return setSubContractCOs(newSubContractCOs);
      }
      case 'nonHoldbackItems':
        return setNonHoldbackValues(updatedData);
      default:
        return setSectionValueMap((prev) => ({
          ...prev,
          [type]: rebuildSovBucket(updatedData),
        }));
    }
  }, [projectId, templateId, subContractCOs]);

  const onSetSubItems = useCallback((type, parentRowId, newChildren) => {
    const updatedChildren = renumberSovRows(newChildren);

    dispatch(swapRowsProjectScheduleOfValues({
      projectId,
      templateId,
      updatedRows: updatedChildren.map(ScheduleOfValues.stripSovTreeFields),
    }));

    if (type === BASE_PANEL) {
      setBaseContractValues((prev) => rebuildSovBucket(prev, parentRowId, updatedChildren));
      return;
    }

    setSectionValueMap((prev) => ({
      ...prev,
      [type]: rebuildSovBucket(prev[type] ?? [], parentRowId, updatedChildren),
    }));
  }, [projectId, templateId]);

  const onMoveBetweenTables = useCallback((
    sourceIndex,
    targetIndex,
    sourceTableType,
    targetTableType,
  ) => {
    const sourceData = (
      sourceTableType === 'changeOrders' ? changeOrderValues : sectionValueMap[sourceTableType]
    ) ?? [];
    const targetData = (
      targetTableType === 'changeOrders' ? changeOrderValues : sectionValueMap[targetTableType]
    ) ?? [];

    const sourceRow = sourceData[sourceIndex];

    if (sourceRow?.hasSubItems && targetTableType === 'changeOrders') {
      notification.warn({
        message: 'Cannot move row',
        description: 'Rows with sub-items cannot be moved into Changes.',
      });
      return;
    }

    const sourceFiltered = sourceData.filter((i) => i !== sourceRow);
    const newSectionId = targetTableType === 'changeOrders' ? null : targetTableType;

    const newSourceData = renumberSovRows(sourceFiltered);

    const newTargetData = renumberSovRows([
      ...targetData.slice(0, targetIndex),
      sourceRow,
      ...targetData.slice(targetIndex),
    ]).map((item, index) => {
      if (index !== targetIndex) return item;
      return {
        ...item,
        isChangeOrder: targetTableType === 'changeOrders',
        updatedField: 'section',
        sectionId: newSectionId,
      };
    });

    // Sub-items follow their parent into the new section.
    const movedSubItems = (sourceRow?.children ?? []).map((child) => ({
      ...child,
      isChangeOrder: false,
      isSubContract: false,
      excludeHoldback: !!child.excludeHoldback,
      updatedField: 'section',
      sectionId: newSectionId,
    }));

    dispatch(swapRowsProjectScheduleOfValues({
      projectId,
      templateId,
      updatedRows: newSourceData
        .concat(newTargetData, movedSubItems)
        .map(ScheduleOfValues.stripSovTreeFields),
    }));

    const sourceRowId = ScheduleOfValues.getSovRowId(sourceRow);

    if (sourceTableType === 'changeOrders') {
      setChangeOrderValues(newSourceData);
      setSectionValueMap((prev) => ({
        ...prev,
        [targetTableType]: rebuildSovBucket(newTargetData, sourceRowId, movedSubItems),
      }));
    } else if (targetTableType === 'changeOrders') {
      setChangeOrderValues(newTargetData.map(ScheduleOfValues.stripSovTreeFields));
      setSectionValueMap((prev) => ({
        ...prev,
        [sourceTableType]: rebuildSovBucket(newSourceData),
      }));
    } else {
      setSectionValueMap((prev) => ({
        ...prev,
        [sourceTableType]: rebuildSovBucket(newSourceData),
        [targetTableType]: rebuildSovBucket(newTargetData, sourceRowId, movedSubItems),
      }));
    }
  }, [projectId, templateId, changeOrderValues, sectionValueMap]);

  const onPreviewClick = useCallback((index) => {
    const file = files[index];

    if (file) {
      if (file instanceof File) {
        setSelectedFile({
          file,
          type: getFileType(file),
        });
      } else {
        setSelectedFile({
          ...file,
          type: getFileType(file),
        });
      }
    }
  }, [files]);

  const addFile = (file) => {
    setFiles([...files, file]);
  };

  const updateFile = useCallback((oldFile, updatedFile) => {
    if (!updatedFile) return clearSelectedFile();
    const newFiles = files.map((file) => {
      if (file === oldFile) return updatedFile;
      return file;
    });
    setFiles(newFiles);
    clearSelectedFile();
    return null;
  }, [files, clearSelectedFile]);

  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const onSubmitSuccess = (snapshot) => {
    setSOVSnapshot(snapshot);
    setVisibleDrawer('export');
  };

  const getSovSnapshot = useCallback((timestamp, submissionId) => {
    const snapshotScheduleOfValues = filterScheduleOfValues(
      scheduleOfValues,
      timestamp,
      submissionId,
    );

    const {
      newBaseContractValues,
      newSubContractValues,
      newSubContractCOs,
      newChangeOrderValues,
      newNonHoldbackItems,
      newLiveSummaryValue,
      sectionValueMap: newSectionValueMap,
    } = scheduleOfValuesData({
      projectId,
      templateId,
      totalBaseContractValue,
      holdbackPercentage,
      scheduleOfValues: snapshotScheduleOfValues,
      scheduleOfValueSectionMap,
      submissionId,
      filterPastBillEdits: false,
    });

    const snapshot = {
      baseContractValues: newBaseContractValues,
      subContractCOs: newSubContractCOs,
      subContractValues: newSubContractValues,
      changeOrderValues: newChangeOrderValues,
      nonHoldbackValues: newNonHoldbackItems,
      liveSummaryValues: [newLiveSummaryValue],
      sectionValueMap: newSectionValueMap,
      timestamp,
    };

    return snapshot;
  }, [scheduleOfValues, projectId, templateId, scheduleOfValueSectionMap]);

  const onLogPdfClicked = useCallback((record) => {
    const {
      id,
      timestamp,
      isHoldbackInvoice,
    } = record;

    const snapshot = getSovSnapshot(timestamp, id);

    if (isHoldbackInvoice) {
      setIsInvoiceHoldback(true);
    }

    setSOVSnapshot(snapshot);
    setVisibleDrawer('export');
  }, [getSovSnapshot]);

  const onLogEditClicked = useCallback((record) => {
    setSelectedSovBill(record);
    setVisibleDrawer('past');
  }, []);

  const onRevertHoldback = useCallback((record) => {
    const { id: logId, holdbackAmount } = record;
    CustomConfirmModal({
      title: 'Revert Holdback',
      content: (
        <div>
          <p>
            Are you sure you want to revert the holdback amount of
            {' '}
            {currencyFormatter(holdbackAmount)}
            {' '}
            from this invoice?
          </p>
          <p style={{ fontWeight: 650 }}>This action is irreversible.</p>
        </div>
      ),
      okText: 'Revert',
      onOk: async () => {
        await dispatch(revertProjectScheduleOfValuesHoldbackInvoice(projectId, { logId }));
      },
    });
  }, [projectId]);

  useEffect(() => {
    if (selectedSovBill) {
      const snapshot = getSovSnapshot(selectedSovBill.timestamp, selectedSovBill.id);
      setSelectedSovBillData(snapshot);
    }
  }, [selectedSovBill, getSovSnapshot]);

  const onHoldbackToggled = async (checked) => {
    const result = await dispatch(updateIndividualProject({
      projectId,
      payload: {
        details: {
          holdbackPercentage: checked ? DEFAULT_HOLDBACK_AMOUNT : 0,
        },
      },
    }));

    if (!result) {
      return;
    }

    setCurrentHoldbackPercentage(checked ? DEFAULT_HOLDBACK_AMOUNT : 0);
  };

  const toggleLockedStatus = useCallback(async () => {
    const unlockProject = async () => {
      if (await dispatch(updateIndividualProject({
        projectId,
        payload: {
          details: {
            isScheduleOfValueLocked: !isLocked,
          },
        },
      }))) {
        setIsLocked(!isLocked);
      }
    };

    if (isLocked) {
      return new Promise((resolve) => {
        CustomConfirmModal({
          title: 'Unlock Confirmation',
          content: (
            <p>
              Are you sure you wish to unlock the schedule of value settings?
              <br />
              <br />
              Changing the schedule of value settings may cause
              inaccurate calculations on existing rows.
            </p>
          ),
          okText: 'Unlock',
          cancelText: 'Cancel',
          async onOk() {
            resolve(await unlockProject());
          },
          onCancel() {
            resolve();
          },
        });
      });
    }

    return unlockProject();
  }, [isLocked]);

  const getPanelButton = ({ onClick, disabled = false }) => {
    const onButtonClick = (event) => {
      // Prevent panel from collapsing;
      event.stopPropagation();
      onClick();
    };

    return (
      <OnTraccrButton
        title="Add Row"
        onClick={onButtonClick}
        disabled={disabled}
      />
    );
  };

  const getMaxItemNumber = (drawer) => {
    if (!drawer) return 1;
    switch (drawer) {
      case SUB_ITEM_DRAWER:
        return (subItemParent?.children?.length ?? 0) + 1;
      case 'addRowBaseContract':
        return newItemNumbers.baseContract;
      case 'addRowSubContract':
        return newItemNumbers.subContract;
      case 'addRowChangeOrder':
        return newItemNumbers.changeOrder;
      case 'addRowNonHoldbackItems':
        return newItemNumbers.nonHoldback;
      default: {
        const sectionId = drawer.split('addRow')?.[1];
        return newItemNumbers[sectionId] || 1;
      }
    }
  };

  const onSectionDelete = (section) => (
    CustomConfirmModal({
      title: `Delete Section: ${section.name}`,
      content: (
        <p>
          Are you sure you wish to delete this section?
          <br />
          <br />
          Deleting this section will remove all rows associated with it.
        </p>
      ),
      okText: 'Delete',
      cancelText: 'Cancel',
      async onOk() {
        await dispatch(deleteScheduleOfValueSection({
          projectId,
          templateId,
          sectionId: section.id,
        }));
      },
    })
  );

  const nextSectionOrderIndex = useMemo(() => {
    const lastSection = scheduleOfValueSections[scheduleOfValueSections.length - 1];
    return lastSection ? lastSection.orderIndex + 1 : 0;
  }, [scheduleOfValueSections]);

  const hasScheduleOfValues = useMemo(() => Object.keys(scheduleOfValues).length > 0, [scheduleOfValues]);

  return (
    <>
      <div className="project-progress-container contract-container" style={{ ...containerStyle }}>
        { projectId && (
          <div className="project-contract-fixed-header">
            <div className="project-contract-header">
              <h3>Schedule of Values</h3>
              {
                !isReadOnly && (
                  <Form.Item className="project-contract-switch">
                    <OnTraccrSwitch
                      value={!!currentHoldbackPercentage}
                      onChange={onHoldbackToggled}
                      disabled={isScheduleOfValueLocked}
                      title="Toggle Holdback"
                    />
                  </Form.Item>
                )
              }
              {
                !isReadOnly && (
                  <OnTraccrButton
                    title={isScheduleOfValueLocked ? 'Unlock' : 'Lock'}
                    icon={isScheduleOfValueLocked ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={toggleLockedStatus}
                  />
                )
              }
              {
                !isReadOnly && (
                  <OnTraccrButton
                    title="Reset"
                    type="back"
                    icon={<RedoOutlined />}
                    disabled={!hasScheduleOfValues}
                    onClick={() => setVisibleDrawer('reset')}
                  />
                )
              }
              {
                !isReadOnly && (
                  <OnTraccrButtonDropdown
                    menuItems={IMPORT_MENU_ITEMS}
                    onMenuClick={({ key }) => setVisibleDrawer(key)}
                    disabled={hasScheduleOfValues}
                    showOpenCloseIcon={false}
                    trigger={(
                      <OnTraccrButton
                        title="Import"
                        icon={<ImportOutlined />}
                        disabled={hasScheduleOfValues}
                      />
                    )}
                  />
                )
              }
              {
                !isReadOnly && (
                  <OnTraccrButton
                    title="Export"
                    icon={<ExportOutlined />}
                    onClick={() => setVisibleDrawer('export')}
                  />
                )
              }
              <OnTraccrButton
                title="Log"
                type="back"
                onClick={() => setShowLogDrawer(true)}
              />
              <span style={{ marginLeft: 5 }}>
                <ScheduleOfValuesColumnChanger
                  value={visibleColumns}
                  onChange={onColumnVisibilityChange}
                />
              </span>
            </div>
            <Table
              columns={liveSummaryColumns}
              dataSource={liveSummaryValues}
              pagination={false}
              rowKey="projectId"
              className="live-summary-table"
            />
          </div>
        )}
        <div className="project-contract-body">
          <DndProvider backend={HTML5Backend}>
            <Collapse
              activeKey={activePanels}
              onChange={setActivePanels}
              className="project-sov-collapse"
            >
              <Panel
                header="Base Contract"
                key="baseContract"
                extra={!isReadOnly && getPanelButton({ disabled: isLocked, onClick: () => switchDrawer('addRowBaseContract') })}
              >
                <DraggableTable
                  columns={baseContractColumns}
                  dataSource={baseContractValues}
                  pagination={false}
                  expandable={treeExpandable}
                  rowClassName={baseContractRowClassName}
                  type={BASE_PANEL}
                  setData={onSetData}
                  setSubItems={onSetSubItems}
                  isDraggable={!isReadOnly && !isScheduleOfValueLocked}
                  enableCrossTableDrag
                />
              </Panel>
              <Panel
                header="Sub-Contracts"
                key={SUB_CONTRACT_PANEL}
                extra={!isReadOnly && getPanelButton({ disabled: isLocked, onClick: () => switchDrawer('addRowSubContract') })}
              >
                <DraggableTable
                  columns={subcontractColumns}
                  dataSource={subContractsWithTotalChanges}
                  pagination={false}
                  expandable={{
                    defaultExpandAllRows: true,
                  }}
                  type="subContract"
                  setData={onSetData}
                  isDraggable={!isReadOnly && !isScheduleOfValueLocked}
                  enableCrossTableDrag
                />
              </Panel>
              <Panel
                header="Changes"
                key={CHANGES_PANEL}
                extra={!isReadOnly && getPanelButton({ onClick: () => switchDrawer('addRowChangeOrder') })}
              >
                <DraggableTable
                  columns={changesColumns}
                  dataSource={changeOrderValues}
                  pagination={false}
                  expandable={{
                    defaultExpandAllRows: true,
                  }}
                  isDraggable={!isReadOnly && !isScheduleOfValueLocked}
                  type="changeOrders"
                  setData={onSetData}
                  enableCrossTableDrag
                  dropKeyMap={scheduleOfValueSectionMap}
                  onMoveBetweenTables={onMoveBetweenTables}
                />
              </Panel>
              { currentHoldbackPercentage && (
                <Panel
                  header="Non-Holdback Items"
                  key={NON_HOLDBACK_PANEL}
                  extra={!isReadOnly && getPanelButton({ onClick: () => switchDrawer('addRowNonHoldbackItems') })}
                >
                  <DraggableTable
                    columns={nonHoldbackColumns}
                    dataSource={nonHoldbackValues}
                    pagination={false}
                    expandable={{
                      defaultExpandAllRows: true,
                    }}
                    isDraggable={!isReadOnly && !isScheduleOfValueLocked}
                    type="nonHoldbackItems"
                    setData={onSetData}
                    enableCrossTableDrag
                  />
                </Panel>
              )}
              {scheduleOfValueSections.map((section) => (
                <Panel
                  header={section.name}
                  key={section.id}
                  extra={
                    !isReadOnly && (
                      <>
                        <SettingOutlined
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAddSectionModal();
                            setSelectedSection(section);
                          }}
                        />
                        <DeleteOutlined
                          style={{ color: Colors.ONTRACCR_RED, paddingLeft: 10, paddingRight: 10 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSectionDelete(section);
                          }}
                        />
                        {getPanelButton({
                          onClick: () => {
                            setSelectedSection(section);
                            switchDrawer(`addRow${section.id}`);
                          },
                        })}
                      </>
                    )
                  }
                >
                  <DraggableTable
                    columns={sectionColumnMap[section.id] || columns}
                    dataSource={sectionValueMap[section.id] || []}
                    pagination={false}
                    expandable={treeExpandable}
                    rowClassName={sectionRowClassNameMap[section.id]}
                    type={section.id}
                    setData={onSetData}
                    setSubItems={onSetSubItems}
                    isDraggable={!isReadOnly && !isScheduleOfValueLocked}
                    enableCrossTableDrag
                    dropKeyMap={scheduleOfValueSectionMap}
                    onMoveBetweenTables={onMoveBetweenTables}
                  />
                </Panel>
              ))}
              <OnTraccrButton
                title="Add Section"
                icon={<PlusOutlined />}
                style={{ width: '100%', borderRadius: 0 }}
                onClick={toggleAddSectionModal}
              />
            </Collapse>
          </DndProvider>
        </div>
      </div>
      <ScheduleOfValuesAddRow
        templateId={templateId}
        liveSummaryValues={liveSummaryValues}
        newItemNumber={getMaxItemNumber(visibleDrawer)}
        isChangeOrder={visibleDrawer === 'addRowChangeOrder'}
        isNonHoldback={visibleDrawer === 'addRowNonHoldbackItems'}
        parentRecord={visibleDrawer === SUB_ITEM_DRAWER ? subItemParent : null}
        visible={visibleDrawer && visibleDrawer.includes('addRow')}
        onClose={onCloseDrawer}
        handleSubmit={handleSubmit}
        isLoading={isLoading}
      />
      <ScheduleOfValuesLog
        logs={logValues}
        visible={showLogDrawer}
        onClose={() => {
          onCloseDrawer();
          setShowLogDrawer(false);
        }}
        onPdf={onLogPdfClicked}
        onEdit={onLogEditClicked}
        onHistory={onHistoryClick}
        onRevertHoldback={onRevertHoldback}
      />
      <ScheduleOfValuesSubmissionReview
        projectId={projectId}
        baseContractValues={baseContractValues}
        changeOrderValues={changeOrderValues}
        liveSummaryValues={liveSummaryValues}
        nonHoldbackValues={nonHoldbackValues}
        dropKeyMap={scheduleOfValueSectionMap}
        sectionValueMap={sectionValueMap}
        subContractsWithTotalChanges={subContractsWithTotalChanges}
        subContractCOs={subContractCOs}
        visible={visibleDrawer === 'review'}
        onClose={onCloseDrawer}
        files={files}
        onPreviewClick={onPreviewClick}
        removeFile={removeFile}
        addFile={addFile}
        setFiles={setFiles}
        updateFile={updateFile}
        selectedFile={selectedFile}
        clearSelectedFile={clearSelectedFile}
        currentHoldbackPercentage={currentHoldbackPercentage}
        onSubmitSuccess={onSubmitSuccess}
        visibleColumns={visibleColumns}
      />
      <ScheduleOfValuesPastBillReview
        snapshot={selectedSovBillData}
        visible={visibleDrawer === 'past'}
        onClose={onCloseDrawer}
        files={files}
        onPreviewClick={onPreviewClick}
        removeFile={removeFile}
        addFile={addFile}
        setFiles={setFiles}
        updateFile={updateFile}
        selectedFile={selectedFile}
        clearSelectedFile={clearSelectedFile}
        currentHoldbackPercentage={currentHoldbackPercentage}
      />
      <HistoryDrawer
        visible={visibleDrawer === 'history'}
        record={selectedRecord}
        onClose={onCloseDrawer}
        isReadOnly={isReadOnly}
        files={files}
        onPreviewClick={onPreviewClick}
        removeFile={removeFile}
        addFile={addFile}
        setFiles={setFiles}
        updateFile={updateFile}
        selectedFile={selectedFile}
        clearSelectedFile={clearSelectedFile}
      />
      <ScheduleOfValuesPDFPreview
        projectId={projectId}
        templateId={templateId}
        baseContractValues={
          sovSnapshot
            ? sovSnapshot.baseContractValues
            : baseContractValues
        }
        subContractValues={
          sovSnapshot
            ? sovSnapshot.subContractValues
            : subContractValues
        }
        changeOrderValues={
          sovSnapshot
            ? sovSnapshot.changeOrderValues
            : changeOrderValues
        }
        subContractCOs={
          sovSnapshot
            ? sovSnapshot.subContractCOs
            : subContractCOs
        }
        nonHoldbackValues={
          sovSnapshot
            ? sovSnapshot.nonHoldbackValues
            : nonHoldbackValues
        }
        liveSummaryValues={
          sovSnapshot
            ? sovSnapshot.liveSummaryValues
            : liveSummaryValues
        }
        sectionValueMap={
          sovSnapshot
            ? sovSnapshot.sectionValueMap
            : sectionValueMap
        }
        currentHoldbackPercentage={currentHoldbackPercentage}
        visible={visibleDrawer === 'export'}
        onClose={onCloseDrawer}
        sovTaxPercentage={sovTaxPercentage}
        divisionId={divisionId}
        isInvoiceHoldback={isInvoiceHoldback}
        setIsInvoiceHoldback={setIsInvoiceHoldback}
      />
      <ScheduleOfValuesTemplateImport
        visible={visibleDrawer === TEMPLATE_IMPORT_DRAWER}
        onClose={onCloseDrawer}
        projectId={projectId}
      />
      <ScheduleOfValuesExcelImportDrawer
        visible={visibleDrawer === EXCEL_IMPORT_DRAWER}
        onClose={onCloseDrawer}
        projectId={projectId}
      />
      <ProjectScheduleOfValuesSubContract
        visible={visibleDrawer === 'sub-contract'}
        onClose={onCloseDrawer}
        record={selectedRecord}
        isReadOnly={isReadOnly}
        liveSummaryValues={liveSummaryValues}
        isScheduleOfValueLocked={isScheduleOfValueLocked}
        onDeleteClick={onDeleteClick}
        templateId={templateId}
        onAddChangeOrder={handleSubmit}
        newItemNumbers={newItemNumbers}
        subContractCOs={subContractCOs}
        files={files}
        onPreviewClick={onPreviewClick}
        removeFile={removeFile}
        addFile={addFile}
        setFiles={setFiles}
        updateFile={updateFile}
        selectedFile={selectedFile}
        clearSelectedFile={clearSelectedFile}
        onSetData={onSetData}
        setSelectedNoteRow={setSelectedNoteRow}
        isLoading={isLoading}
      />
      <ScheduleOfValuesAddSectionModal
        visible={isAddSectionModalToggled}
        section={selectedSection}
        onClose={() => {
          toggleAddSectionModal();
          setSelectedSection();
        }}
        orderIndex={nextSectionOrderIndex}
        projectId={projectId}
        templateId={templateId}
      />
      <ScheduleOfValuesNotesModal
        projectId={projectId}
        templateId={templateId}
        selectedRow={selectedNoteRow}
        visible={!!selectedNoteRow}
        onClose={() => setSelectedNoteRow()}
      />
      <ProjectScheduleOfValuesResetDrawer
        visible={visibleDrawer === 'reset'}
        onClose={onCloseDrawer}
        projectId={projectId}
      />
    </>
  );
}

ProjectScheduleOfValues.propTypes = {
  projectId: PropTypes.string,
  templateId: PropTypes.string,
  containerStyle: PropTypes.shape({}),
  readOnly: PropTypes.bool,
};

ProjectScheduleOfValues.defaultProps = {
  projectId: null,
  templateId: null,
  containerStyle: {},
  readOnly: false,
};
